import React from "react";
import SEO from "./SEO";
import Showroom from "./Showroom";
import ContactUs from "./ContactUs";
import BackgroundImg from "../assets/background.jpg";
import logo from "../assets/yamaha-logo.png";
import { Helmet } from "react-helmet-async";
import TestDriveButton from "./TestDriveButton";


const JanakpuriShowroom = () => {
  // Localized JSON-LD Schema markup for Janakpuri audience
  const janakpuriSchema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "name": "Excellent Yamaha Showroom - Serving Janakpuri",
    "image": "https://excellentmotors.in/favicon.png",
    "telephone": "+91 7982920138",
    "url": "https://excellentmotors.in/yamaha-showroom-janakpuri",
    "description": "Authorized Yamaha showroom and dealer serving Janakpuri, Vikaspuri, Tilak Nagar, Hari Nagar, and West Delhi. Explore FZ, MT-15, R15 V4, and Aerox 155 models with easy financing.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "RR-11, near Peeragarhi metro station, Paschim Vihar (Closest Branch for Janakpuri)",
      "addressLocality": "New Delhi",
      "addressRegion": "Delhi",
      "postalCode": "110087",
      "addressCountry": "IN"
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      <Helmet>
        <title>Authorized Yamaha Showroom near Janakpuri | West Delhi Dealer</title>
        <meta name="description" content="Looking for an authorized Yamaha dealer near Janakpuri, Vikaspuri, or Tilak Nagar? Excellent Motors is West Delhi's premier Yamaha partner. Book your test ride today!" />
        <link rel="canonical" href="https://excellentmotors.in/yamaha-showroom-janakpuri" />
      </Helmet>
      <SEO
        title="Authorized Yamaha Showroom near Janakpuri | West Delhi Dealer"
        description="Looking for an authorized Yamaha dealer near Janakpuri, Vikaspuri, or Tilak Nagar? Excellent Motors is West Delhi's premier Yamaha partner. Book your test ride today!"
        keywords="Yamaha showroom near Janakpuri, Yamaha showroom Vikaspuri, Yamaha Tilak Nagar, Yamaha showroom West Delhi, Yamaha MT-15 Janakpuri, Yamaha R15 Janakpuri"
        canonical="https://excellentmotors.in/yamaha-showroom-janakpuri"
        schema={janakpuriSchema}
      />

      {/* Janakpuri Localized Hero Section */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden border-b border-gray-800">
        {/* Background Image layer with 30% opacity */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30 z-0"
          style={{ backgroundImage: `url(${BackgroundImg})` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-gray-900/90 to-blue-950/95 z-0"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-6">
          <img src={logo} alt="Yamaha R-DNA Logo" className="h-16 w-auto mx-auto mb-4 animate-bounce" />
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-wide uppercase text-white leading-tight">
            Authorized Yamaha Showroom <br className="hidden sm:inline" />
            <span className="text-yellow-400">Serving Janakpuri & Vikaspuri</span>
          </h1>
          <p className="text-gray-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Welcome to <strong>Excellent Motors</strong>, West Delhi's highest-rated authorized dealer, serving the premium riding communities of <strong>Janakpuri, Vikaspuri, Hari Nagar, and Tilak Nagar</strong>. Discover extreme performance, sporty technology, and the finest selection of Yamaha bikes and scooters.
          </p>

          {/* Quick trust metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto pt-8 text-sm text-gray-200">
            <div className="bg-gradient-to-br from-blue-900/95 via-blue-950 to-indigo-950/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Down Payment</h3>
              <p className="text-white font-medium">Starts as low as ₹10,000* on city scooters!</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-900/95 via-blue-950 to-blue-950/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Exchange Offer</h3>
              <p className="text-white font-medium">Exchange bonus up to ₹3,000 on your old ride.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-950 via-indigo-950 to-blue-900/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Instant Finance</h3>
              <p className="text-white font-medium">Approval on the spot with minimum documents.</p>
            </div>
          </div>
        </div>
      </section>

      {/* NEW ATTRACTIVE FEATURE: Trending Models & EMI Estimates Card Grid */}
      <section className="py-16 px-6 max-w-6xl mx-auto text-center">
        <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-2 uppercase tracking-wide">
          Trending Yamaha Models & Finance Estimates
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto mb-10 text-sm sm:text-base">
          Browse the most popular Yamaha models in Janakpuri and check out our lowest estimated monthly EMI plans:
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Card 1: MT-15 */}
          <div className="group bg-gradient-to-br from-blue-950 via-slate-900 to-indigo-950/40 border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-500 hover:from-blue-900/30 hover:to-indigo-900/30 transition-all duration-300 shadow-lg flex flex-col justify-between transform hover:scale-[1.02]">
            <div>
              {/* Product Image Frame */}
              <div className="relative h-48 w-full overflow-hidden bg-black/50 border-b border-gray-850">
                <img 
                  src="/images/mt15v2.jpg" 
                  alt="Yamaha MT-15 V2.0" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                />
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-red-600 text-white font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider inline-block shadow-md">
                    Top Seller
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4 text-left">
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Yamaha MT-15 V2.0</h3>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Aggressive street fighter with Bi-Functional LED headlamp and Traction Control System.
                </p>
                <div className="border-t border-b border-gray-800 py-3 my-4 space-y-1.5">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Min. Down Payment:</span>
                    <span className="text-yellow-400 font-bold">₹15,000*</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Est. Monthly EMI:</span>
                    <span className="text-white font-bold">₹4,299/mo*</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2">
              <a href="#contact" className="block text-center bg-blue-800 hover:bg-blue-900 text-white font-bold py-2.5 rounded-lg transition text-sm">
                Inquire Price &rarr;
              </a>
            </div>
          </div>

          {/* Card 2: R15 V4 */}
          <div className="group bg-gradient-to-br from-indigo-950 via-slate-900 to-blue-950/40 border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-500 hover:from-indigo-900/30 hover:to-blue-900/30 transition-all duration-300 shadow-lg flex flex-col justify-between transform hover:scale-[1.02]">
            <div>
              {/* Product Image Frame */}
              <div className="relative h-48 w-full overflow-hidden bg-black/50 border-b border-gray-850">
                <img 
                  src="/images/r15V4-blue.jpg" 
                  alt="Yamaha R15 V4" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                />
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-blue-600 text-white font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider inline-block shadow-md">
                    R-DNA Legend
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4 text-left">
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Yamaha R15 V4</h3>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Aerodynamic supersport monster equipped with Quick Shifter and Dual Channel ABS.
                </p>
                <div className="border-t border-b border-gray-800 py-3 my-4 space-y-1.5">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Min. Down Payment:</span>
                    <span className="text-yellow-400 font-bold">₹19,999*</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Est. Monthly EMI:</span>
                    <span className="text-white font-bold">₹4,899/mo*</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2">
              <a href="#contact" className="block text-center bg-blue-800 hover:bg-blue-900 text-white font-bold py-2.5 rounded-lg transition text-sm">
                Inquire Price &rarr;
              </a>
            </div>
          </div>

          {/* Card 3: Aerox 155 */}
          <div className="group bg-gradient-to-br from-blue-950 via-indigo-950 to-slate-950/40 border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-500 hover:from-blue-900/30 hover:to-slate-900/30 transition-all duration-300 shadow-lg flex flex-col justify-between transform hover:scale-[1.02]">
            <div>
              {/* Product Image Frame */}
              <div className="relative h-48 w-full overflow-hidden bg-black/50 border-b border-gray-850">
                <img 
                  src="/images/aerox15SVs-Sc.jpg" 
                  alt="Yamaha Aerox 155" 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                />
                <div className="absolute top-4 left-4 z-10">
                  <span className="bg-yellow-500 text-blue-950 font-extrabold text-xs px-3 py-1 rounded-full uppercase tracking-wider inline-block shadow-md">
                    Maxi Scooter
                  </span>
                </div>
              </div>
              <div className="p-6 space-y-4 text-left">
                <h3 className="text-xl font-bold text-white uppercase tracking-wide">Yamaha Aerox 155</h3>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Power-packed liquid-cooled 155cc engine scooter with Bluetooth connectivity and ABS.
                </p>
                <div className="border-t border-b border-gray-800 py-3 my-4 space-y-1.5">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Min. Down Payment:</span>
                    <span className="text-yellow-400 font-bold">₹12,000*</span>
                  </div>
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-400">Est. Monthly EMI:</span>
                    <span className="text-white font-bold">₹3,499/mo*</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 pt-2">
              <a href="#contact" className="block text-center bg-blue-800 hover:bg-blue-900 text-white font-bold py-2.5 rounded-lg transition text-sm">
                Inquire Price &rarr;
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local Advertising Copy Block */}
      <section className="py-16 px-6 max-w-5xl mx-auto border-t border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold border-l-4 border-yellow-500 pl-3">
              Why Janakpuri Riders Choose Excellent Yamaha
            </h2>
            <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
              Living in Janakpuri, Vikaspuri, Tilak Nagar, or Hari Nagar? You are just a stone's throw away from our Peeragarhi and Paschim Vihar showrooms. Excellent Motors provides quick access to Yamaha's world-class engineering, premium financing support, and authentic components.
            </p>
            <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
              Our showrooms adhere strictly to international Yamaha standards, delivering a seamless purchasing experience. From choosing your favorite color to getting instant vehicle delivery, our relationship managers hold your hand through the entire journey.
            </p>
            <ul className="space-y-2.5 text-sm text-gray-300 font-semibold">
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Free Test Rides available on all models</li>
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Authorized Yamaha Technical Academy service support</li>
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Transparent deals with zero hidden charges</li>
            </ul>
          </div>
          <div className="group bg-gradient-to-br from-blue-900 via-indigo-950 to-slate-950 border border-blue-900/40 p-8 rounded-2xl shadow-xl text-center space-y-6 transform transition-all duration-300 hover:shadow-blue-500/10">
            <div className="relative h-44 w-full overflow-hidden rounded-xl bg-gray-950 shadow-inner">
              <img 
                src="/images/r15M.jpg" 
                alt="Yamaha Premium R15M Showcase" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent"></div>
              <div className="absolute bottom-3 left-0 right-0">
                <span className="text-yellow-400 text-xs font-black uppercase tracking-widest bg-blue-950/85 px-3 py-1 rounded-full border border-yellow-500/30">
                  R-DNA Supersport
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-extrabold text-white uppercase tracking-wide">Book Your Dream Yamaha Today</h3>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                Don't wait for your ride. Reserve your test ride or get custom EMI quotes directly from our relationship manager!
              </p>
            </div>
            <a 
              href="#contact" 
              className="bg-yellow-500 hover:bg-yellow-400 text-blue-950 font-extrabold px-6 py-3 rounded-xl inline-block w-full text-center transition transform active:scale-95 shadow-md hover:shadow-yellow-500/20"
            >
              Inquire Now &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* Embedded Showrooms Location tag block */}
      <section className="bg-gray-950/80 py-4 border-t border-b border-gray-850">
        <Showroom />
      </section>

      {/* Embedded Contact Us Form */}
            <ContactUs />
      <TestDriveButton />
    </div>
  );
};

export default JanakpuriShowroom;
